// SPDX-License-Identifier: Apache-2.0

import ETH_LOGO from "../images/assets/ETH_logo.png";
import PRN_LOGO from "../images/assets/PRN_logo.png";
import ROPT_LOGO from "../images/assets/ROPT_logo.png";
import GOE_LOGO from "../images/assets/GOE_logo.png";
import RBY_LOGO from "../images/assets/RBY_logo.png";
import VIA_LOGO from "../images/assets/VIA_logo.png";
import BLC_LOGO from "../images/assets/BLC_logo.png";
import SEP_LOGO from "../images/assets/SEP_logo.png";
import XLM_LOGO from "../images/assets/XLM_logo.png";
import USDC_LOGO from "../images/assets/USDC_logo.png";
import TEST_logo from "../images/assets/TEST_logo.png";
import CKB_logo from "../images/assets/CKB_logo.png";
import Address from "../api/types/address";
import {
  BlockExplorer,
  BlockScout,
  CKBExplorer,
  Etherscan,
  Localhost,
  StellarScout,
} from "./BlockExplorer";
import config from "../config.json";

import { ckbHubAddress } from "./CKBNetwork";

// Re-exported so existing importers keep working; the value and everything
// derived from it live in CKBNetwork.ts.
export { ckbNetwork, omniLockCodeHash } from "./CKBNetwork";

export const connectionTimeoutMS = 10000;
export const defaultTimeoutSeconds = 10000;
export const finishSwapTimeout = 500000;
export const proposingTimeoutSeconds = 10000;
export const challengeDurationSeconds = 600;
export const amountStepWidth = 0.0001;
export const maxRejectReasonLength = 100;
export const feedbackEmail = "feedback@perun.network";
export const discordLink = "https://discord.me/perun";

const StellarChainID = 2n;
const EthereumChainID = 1n;
const CKBChainID = 3n;
const RinkebyChainID = 4n;
const GoerliChainID = 5n;
const KovanChainID = 42n;
const VivianiChainID = 133n;
const BellecourChainID = 134n;
const SepoliaChainID = 11155111n;
const Localhost1337ChainID = 1337n;
const Localhost1338ChainID = 1338n;

const EthereumCode = "ETH";
const RLCCode = "RLC";
const RopstenTokenCode = "ROPT";
const RinkebyTokenCode = "RBY";
const GoerliTokenCode = "GOE";
const GoerliETHTokenCode = "GOEETH";
const PerunTokenCode = "PRN";
const VivianiTokenCode = "VIA";
const BellecourTokenCode = "BLC";
const BellecourETHTokenCode = "xRLC";
const SepoliaTokenCode = "SEPETH";
const StellarLumensCode = "XLM";
const USDCCode = "USDC";
const TestCode = "TEST";
const PRNCCode = "PRNC";
const PRNCode = "PRN";
const CKBCode = "CKB";
const USDICode = "USDI";
const CusdCode = "cusd";

// valid swap Pairs
export const validSwapPairs = [
  // Same-chain Ethereum pair for the mainnet ETH<->USDC product. validPair()
  // matches unordered, so one entry covers both directions.
  ["ETH", "USDC"],
  ["ETH", "RLC"],
  ["ETH", "xRLC"],
  ["ETH", "PRN"],
  ["ETH", "PRNC"],
  ["ETH", "CKB"],
  // Cross-chain ERC-20 pair. Needs the hub to hold USDC for the CKB→USDC
  // direction and CKB for USDC→CKB; the hub prices ERC-20 fees in token units
  // since hub 03e9460.
  ["USDC", "CKB"],
  ["SEPETH", "CKB"],
  ["PRN", "CKB"],
  ["PRNC", "PRN"],
  ["PRNC", "TEST"],
  ["ETH", "XLM"],
  // ["BLC", "SEPETH"],
  // ["xRLC", "RLC"],
  ["PRNC", "XLM"],
  ["ETH", "XLM"],
  ["PRNC", "USDC"],
  ["USDC", "XLM"],
  ["USDC", "SEPETH"],
  ["TEST", "ETH"],
  ["TEST", "USDC"],
  ["TEST", "PRNC"],
  ["TEST", "SEPETH"],
  ["TEST", "PRN"],
  ["TEST", "XLM"],
  ["PRN", "SEPETH"],
  ["XLM", "SEPETH"],
  // ["CKB", "ETH"],
  ["CKB", "USDI"],
  ["CKB", "cusd"],
];

export const ChainNames = new Map<bigint, string>([
  [StellarChainID, "Stellar"],
  [CKBChainID, "Nervos"],
  [EthereumChainID, "Ethereum"],
  [RinkebyChainID, "Rinkeby"],
  [GoerliChainID, "Goerli"],
  [KovanChainID, "Kovan"],
  [VivianiChainID, "Viviani"],
  [BellecourChainID, "Bellecour"],
  [SepoliaChainID, "Sepolia"],
  [Localhost1337ChainID, "Localhost 1337"],
  [Localhost1338ChainID, "Localhost 1338"],
]);

/**
 * The EVM chain this build targets, read from the same baked config whose
 * endpoints the app dials — so a label derived from it cannot disagree with
 * the chain actually in use. The deployed build once said "Sepolia" while
 * dialing mainnet because these names were hardcoded in markup.
 */
export const ethChainID: bigint = BigInt(config.networkIDs[0]);

/**
 * Second per-swap USD ceiling, enforced by the hub on its own leg of the swap
 * as `--volumeLimit` (checkTrade in hub/channel.go). Zero disables the check,
 * which is the same convention the hub uses.
 *
 * Unlike the guardrail — which the UI can recover from the guardrail response,
 * because the hub encodes it in every threshold — this limit appears in no
 * message, so a build can only show it if it carries it. That makes this a copy
 * of VOLUME_LIMIT in .env.mainnet on the relay: change one without the other and
 * the page advertises a ceiling the hub does not apply, or the hub rejects a
 * swap the page said was fine.
 */
export const volumeLimitUSD: number = config.volumeLimitUSD;

/**
 * Whether this build shows the liquidity pool (yield) screen. Off unless the
 * build config sets it, so a build that predates the key — mainnet — never
 * offers a feature its backend and hub do not run.
 */
export const lpEnabled: boolean =
  (config as { lpEnabled?: boolean }).lpEnabled === true;

/**
 * Protection every CKB LP cell created from this site gets, frozen at
 * creation: EnforceMinFee | RequirePrice | SafePrice (2 | 4 | 8), and the
 * safe-price band in CKB per ETH as Q64.64 decimal strings. Empty when the
 * build config has no band — the deposit form then refuses rather than
 * create an unprotected cell.
 */
export const lpPolicyFlags = 14;
export const lpSafePriceMinX64: string =
  (config as { lpSafePriceMinX64?: string }).lpSafePriceMinX64 ?? "";
export const lpSafePriceMaxX64: string =
  (config as { lpSafePriceMaxX64?: string }).lpSafePriceMaxX64 ?? "";

/**
 * The fee the hub charges on a swap, in basis points (its HUB_FEE_RATE). The
 * hub only draws on LP cells whose fee is at most this, so it is the default
 * and the ceiling of the deposit form. Keep in step with the hub by hand, like
 * volumeLimitUSD.
 */
export const hubFeeBps: number =
  (config as { hubFeeBps?: number }).hubFeeBps ?? 0;

/**
 * CKB a swap with CKB needs in the user's Nervos wallet besides the amount:
 * the user opens the channel on Nervos, which locks the channel cell (908 CKB
 * on mainnet and testnet), an 80 CKB padding and, for the change, a minimum
 * cell (61 CKB), plus fees. All of it is returned when the swap completes
 * except the network fee. Per network in the config, since the cell sizes are
 * a property of the deployed contracts.
 */
export const ckbSwapReserve: bigint =
  BigInt((config as { ckbSwapReserveCKB?: number }).ckbSwapReserveCKB ?? 1050) *
  100_000_000n;

/**
 * Display name for the configured EVM network, phrased the way wallets label
 * it: the UI quotes this in MetaMask instructions, and MetaMask calls chain 1
 * "Ethereum Mainnet", not "Ethereum".
 */
export const ethNetworkName: string =
  ethChainID === EthereumChainID
    ? "Ethereum Mainnet"
    : (ChainNames.get(ethChainID) ?? `chain ${ethChainID}`);

export const contractAddresses = new Map<string, string>([
  [
    "53a71f6c20a548186b2e3b14b3cfc12a7eda6596d4b8a243677ec758646e7db3", //TEST
    "CBJ2OH3MECSUQGDLFY5RJM6PYEVH5WTFS3KLRISDM57MOWDENZ63GDRR",
  ],
  [
    "57ae6c738661883a56ea52482680f96d71c399d39bce258911bfaf1d1acd0c34", //PRN
    "CBQ2O3KIX5U2XXP2NJRJJQCSBYNLS56JPKVQW64SMCC5QXQ3T44UXGKQ",
  ],
  [
    "d7928b72c2703ccfeaf7eb9ff4ef4d504a55a8b979fc9b450ea2c842b4d1ce61", //XLM
    "CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC",
  ],
  [
    "5045cd5ec0729a768fd5ad02505852df4f028dce830e5ac52209ba48483b2f01", //center.io USDC
    "CBIELTK6YBZJU5UP2WWQEUCYKLPU6AUNZ2BQ4WWFEIE3USCIHMXQDAMA",
  ],
  [
    "61a76d48bf69abddfa6a6294c0520e1ab977c97aab0b7b926085d85e1b9f394b", //PRNC
    "CBQ2O3KIX5U2XXP2NJRJJQCSBYNLS56JPKVQW64SMCC5QXQ3T44UXGKQ",
  ],
]);

export type Hub = {
  name: string;
  address: string;
  backendID: boolean;
};

// The localhost LP hub operator (= alice). The ws-backend registry lookup is
// EXACT and CASE-SENSITIVE, so this must match — verbatim EIP-55 casing — the
// operator address the running hub registers under (derived from its --ethSK).
// config.json may override it, but the default is the correct alice value
// so local dev and the integration tests resolve the hub even without an
// override; a wrong/missing value resurfaces the backend "hub not found" error.
const LOCALHOST_HUB_ADDRESS = "0x05e71027e7d3bd6261de7634cf50F0e2142067C4";
const localhostHub: Hub = {
  name: (config as { hubName?: string }).hubName ?? "Perun Localhost Hub #1",
  address:
    (config as { hubAddress?: string }).hubAddress ?? LOCALHOST_HUB_ADDRESS,
  backendID: true,
};

export const Hubs = new Map<bigint, Hub>([
  [
    StellarChainID,
    {
      name: "Perun Stellar Testnet",
      address: "GBUHWM5JBKWSWV42ITG4X22EKBYAVM4MYAJ2USQ2ICLYWIBONI72SOFE", //"GCLNCGAZXXX4A6EEBFYMVTVWLWBXRZ5JUMUM4JNTEM2QP2MB32YN6KZR", //"GBUHWM5JBKWSWV42ITG4X22EKBYAVM4MYAJ2USQ2ICLYWIBONI72SOFE"
      backendID: false,
    },
  ],
  [
    EthereumChainID,
    {
      // The cross-chain mainnet hub, derived from HUB_ETH_SK (keystore
      // ~/.perunx/hub). Replaced the OLD single-chain product's hub
      // 0x700c07D2… on 2026-08-06 — the hub rejects any request whose Hub
      // field is not one of its own addresses, so a stale value here produces
      // confusing rejections rather than an obvious failure.
      //
      // The legacy hub is still live on its own stack; do not reintroduce it
      // here. Its key has been exposed for months and is on the rotation queue.
      name: "Perun Ethereum Hub #1",
      address: "0xEC9310804e4b56E00e5C5112D8c420E3821B4F3b",
      backendID: true,
    },
  ],
  [
    CKBChainID,
    {
      name: "Perun CKB Hub #1",
      address: ckbHubAddress,
      backendID: false,
    },
  ],
  [
    RinkebyChainID,
    {
      name: "Perun Rinkeby Hub #1",
      address: "0x8F6881803aE364A4E6E7D44e0CC6049635266789",
      backendID: true,
    },
  ],
  [
    GoerliChainID,
    {
      name: "Perun Goerli Hub #1",
      address: "0x8F6881803aE364A4E6E7D44e0CC6049635266789",
      backendID: true,
    },
  ],
  [
    VivianiChainID,
    {
      name: "Perun Viviani Hub #1",
      address: "0x8F6881803aE364A4E6E7D44e0CC6049635266789",
      backendID: true,
    },
  ],
  [
    BellecourChainID,
    {
      name: "Perun Bellecour Hub #1",
      address: "0x700c07D26209E4DA5Df30cAd20D4209e707Adb80",
      backendID: true,
    },
  ],
  [
    SepoliaChainID,
    {
      name: "Perun Sepolia Hub #1",
      address: "0x8F6881803aE364A4E6E7D44e0CC6049635266789",
      backendID: true,
    },
  ],
  [
    SepoliaChainID.valueOf() + StellarChainID.valueOf(),
    {
      name: "Perun Sepolia, stellar Hub #1",
      address: "0xD9e2F79902335de74985E58415c49D5303760688",
      backendID: true,
    },
  ],
  [Localhost1337ChainID, localhostHub],
  [Localhost1338ChainID, localhostHub],
]);

export const BlockExplorers = new Map<bigint, BlockExplorer>([
  [EthereumChainID, new Etherscan()],
  [RinkebyChainID, new Etherscan("rinkeby")],
  [GoerliChainID, new Etherscan("goerli")],
  [KovanChainID, new Etherscan("kovan")],
  [VivianiChainID, new BlockScout("https://blockscout-viviani.iex.ec")],
  [BellecourChainID, new BlockScout("https://blockscout-bellecour.iex.ec")],
  [SepoliaChainID, new Etherscan("sepolia")],
  [Localhost1337ChainID, new Localhost()],
  [Localhost1338ChainID, new Localhost()],
  [StellarChainID, new StellarScout("testnet")],
  [CKBChainID, new CKBExplorer()],
]);

export function getExplorerURL(
  chainID: bigint,
  address: Address | string,
  display: "address" | "token",
  filterAddress?: Address,
): string {
  if (chainID === 0n) {
    chainID = 2n;
  }
  const url = BlockExplorers.get(chainID);
  if (!url) {
    console.log("unknown chain ID:", chainID, address, url);
    throw Error("unknown chain ID");
  }
  if (chainID === 2n) {
    const contractAddress = contractAddresses.get(address as string);
    if (contractAddress) {
      return url.getExplorerURL(contractAddress, display, filterAddress);
    } else {
      return "https://stellar.expert/explorer/testnet";
    }
  }
  return url.getExplorerURL(address, display, filterAddress);
}

// The assets are only identified by their code rather than AssetID because
// these constants only mock getting asset data and should not depend on the
// chainID for the moment, so we assume unique asset codes here.
export const assetLogos = new Map([
  [EthereumCode, ETH_LOGO],
  [RLCCode, ETH_LOGO],
  [PerunTokenCode, PRN_LOGO],
  [RopstenTokenCode, ROPT_LOGO],
  [GoerliTokenCode, GOE_LOGO],
  [GoerliETHTokenCode, GOE_LOGO],
  [RinkebyTokenCode, RBY_LOGO],
  [VivianiTokenCode, VIA_LOGO],
  [BellecourTokenCode, BLC_LOGO],
  [BellecourETHTokenCode, BLC_LOGO],
  [SepoliaTokenCode, SEP_LOGO],
  [StellarLumensCode, XLM_LOGO],
  [USDCCode, USDC_LOGO],
  [PRNCode, PRN_LOGO],
  [TestCode, TEST_logo],
  [PRNCCode, PRN_LOGO],
  [CKBCode, CKB_logo],
]);
export const assetPrecisions = new Map([
  [EthereumCode, 18],
  [RLCCode, 8],
  [PerunTokenCode, 7],
  [RopstenTokenCode, 0],
  [GoerliTokenCode, 0],
  [GoerliETHTokenCode, 8],
  [RinkebyTokenCode, 0],
  [VivianiTokenCode, 0],
  [BellecourTokenCode, 0],
  [BellecourETHTokenCode, 8],
  [SepoliaTokenCode, 8],
  [StellarLumensCode, 7],
  // Ethereum USDC has 6 decimals. This map is keyed by asset code alone, so it
  // cannot also describe Stellar's 7-decimal USDC; mainnet v1 has no Stellar
  // (deprecated), and this value only drives display rounding — the authoritative
  // decimals come from the backend's GetDecimals.
  [USDCCode, 6],
  [PRNCode, 7],
  [TestCode, 7],
  [PRNCCode, 7],
  [CKBCode, 8],
  [USDICode, 6],
  [CusdCode, 8],
]);

// Gas used by the hub's transactions, charged to the user in the swap amounts.
// The hub computes its price check with the same values (calcTxFee in
// perun-x-chain-hub hub/channel.go); keep both in step. The ERC20 values are
// the gas the hub's USDC transactions used on mainnet (median of its receipts
// up to 2026-09-20) plus 10%.

// Estimated gas used for the withdrawal.
export const ethWithdrawal = 47268;
export const erc20Withdrawal = 65305; // measured 59368

// Estimated gas used for the approval.
export const approval = 61114; // measured 55558

// Estimated gas used for the deposit.
export const ethDeposit = 45789;
export const erc20Deposit = 99570; // measured 90518

// Estimated gas used for concludeFinal.
export const concludeSameChain = 217526;
export const concludeCrossChain = 176553;

// Estimated transaction fees on stellar per function call.
export const fund = 270000;
export const close = 232300;
export const withdraw = 250000;
